<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import type { AppUserMenuProps } from '../../models/navigation/UserMenu.model'
import type { NavigationItem } from '~/models/navigation/NavigationItems.model'
import AppIcon from '~/components/elements/AppIcon.vue'
import { useLocalePath, useI18n } from '#i18n'
import { useRoute, navigateTo } from '#imports'
import {
  onClickOutside,
  useEventListener,
  useBreakpoints,
  breakpointsTailwind,
} from '@vueuse/core'

const props = defineProps<AppUserMenuProps>()

const emit = defineEmits<{
  'update:isOpen': [isOpen: boolean]
  itemClick: [item: NavigationItem]
}>()

const { t } = useI18n()

const menuRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLButtonElement | null>(null)
const flyoutMenuRef = ref<HTMLElement | null>(null)
const isMenuOpen = ref(false)
const hasUserImage = computed(() => !!props.userImage)
const route = useRoute()
const localePath = useLocalePath()

const isExternal = (url: string | undefined): boolean => {
  if (!url) return false
  return (
    url.startsWith('http') ||
    url.startsWith('https') ||
    url.startsWith('mailto:')
  )
}

const isUserMenuItemActive = (item: NavigationItem) => {
  if (!item.to) return false
  if (isExternal(item.to)) return false
  const itemPath = localePath(item.to)
  const currentPath = route.path
  return currentPath === itemPath
}

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  emit('update:isOpen', isMenuOpen.value)
}

const closeMenu = () => {
  if (!isMenuOpen.value) return
  isMenuOpen.value = false
  emit('update:isOpen', false)
}

onClickOutside(
  menuRef,
  () => {
    closeMenu()
  },
  {
    ignore: [buttonRef],
  }
)

const handleItemClick = (item: NavigationItem) => {
  closeMenu()
  emit('itemClick', item)

  if (item.action) {
    item.action()
  } else if (item.to) {
    if (isExternal(item.to)) {
      const target = item.to.startsWith('mailto:') ? '_self' : '_blank'
      window.open(item.to, target)
    } else {
      navigateTo(localePath(item.to))
    }
  }
}

const focusedIndex = ref(-1)
const menuItemRefs = ref(new Map<number, HTMLButtonElement>())

const setMenuItemRef = (
  el: Element | ComponentPublicInstance | null,
  index: number
) => {
  if (el) {
    const element = (el as ComponentPublicInstance).$el || el
    menuItemRefs.value.set(index, element as HTMLButtonElement)
  } else {
    menuItemRefs.value.delete(index)
  }
}

const focusMenuItem = (index: number) => {
  const element = menuItemRefs.value.get(index)
  if (index >= 0 && index < props.menuItems.length && element) {
    focusedIndex.value = index
    nextTick(() => {
      element.focus()
    })
  }
}

useEventListener('keydown', (event: KeyboardEvent) => {
  if (!isMenuOpen.value) return

  switch (event.key) {
    case 'Escape':
      event.preventDefault()
      closeMenu()
      nextTick(() => {
        buttonRef.value?.focus()
      })
      break

    case 'ArrowDown':
      event.preventDefault()
      if (focusedIndex.value < props.menuItems.length - 1) {
        focusMenuItem(focusedIndex.value + 1)
      } else {
        focusMenuItem(0)
      }
      break

    case 'ArrowUp':
      event.preventDefault()
      if (focusedIndex.value > 0) {
        focusMenuItem(focusedIndex.value - 1)
      } else {
        focusMenuItem(props.menuItems.length - 1)
      }
      break

    case 'Home':
      event.preventDefault()
      focusMenuItem(0)
      break

    case 'End':
      event.preventDefault()
      focusMenuItem(props.menuItems.length - 1)
      break

    case 'Tab':
      closeMenu()
      break
  }
})

watch(isMenuOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      focusMenuItem(0)
    })
  } else {
    focusedIndex.value = -1
  }
})

const menuItemClasses = computed(() => {
  return (item: NavigationItem, index: number) => {
    const classes = [
      'block w-full px-4 py-2 text-left',
      'text-base text-black',
      'cursor-pointer',
      'transition-colors duration-150',
      'hover:bg-primary-50',
      'focus:outline-none',
      'focus-visible:relative focus-visible:z-10',
      'focus-visible:bg-primary-50',
      'focus-visible:ring-2 focus-visible:ring-black',
    ]

    if (index === 0) {
      classes.push('rounded-t-lg')
    }

    if (index === props.menuItems.length - 1) {
      classes.push('rounded-b-lg')
    }

    if (isUserMenuItemActive(item)) {
      classes.push('font-bold', 'bg-primary-50')
    }

    return classes
  }
})

const buttonStateClasses = computed(() => {
  const base =
    'flex items-center rounded-lg border border-basics-border transition-all duration-200 focus:ring-[3px] focus:ring-black focus-visible:ring-[3px] focus-visible:ring-black focus:outline-none'

  return isMenuOpen.value
    ? `${base} border-primary-800`
    : `${base} border-accent-06 hover:border-primary-800 hover:bg-primary-50 active:border-primary-800 active:bg-primary-200`
})

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('lg')

const flyoutMenuClasses = computed(() => {
  if (isMobile.value) {
    return 'absolute bottom-full mb-2 left-0 right-0 z-50 w-full overflow-visible rounded-lg bg-white shadow-lg'
  }
  return 'absolute bottom-[8px] right-[-10px] z-50 w-48 translate-x-full translate-y-3 transform overflow-visible rounded-lg bg-white shadow-md'
})

const userMenuButtonLabel = computed(() => t('userMenu.openButtonLabel'))
const flyoutMenuLabel = computed(() => t('userMenu.flyoutLabel'))

const getMenuItemAriaAttributes = (item: NavigationItem) => {
  const attributes: Record<string, string | undefined> = {}

  if (isUserMenuItemActive(item)) {
    attributes['aria-current'] = 'page'
  }

  if (item.to && isExternal(item.to) && !item.to.startsWith('mailto:')) {
    attributes['aria-describedby'] = 'external-link-description'
  }

  return attributes
}
</script>

<template>
  <div ref="menuRef" class="relative" data-testid="user-menu-container">
    <div id="external-link-description" class="sr-only">
      {{ t('userMenu.externalLinkDescription') }}
    </div>

    <button
      ref="buttonRef"
      :class="[
        buttonStateClasses,
        'p-2',
        isCollapsed ? 'justify-center' : 'w-full justify-between',
      ]"
      tabindex="0"
      aria-haspopup="true"
      :aria-expanded="isMenuOpen"
      :aria-label="userMenuButtonLabel"
      data-testid="user-menu-button"
      @click="toggleMenu"
    >
      <div class="flex items-center">
        <img
          v-if="hasUserImage"
          :src="userImage"
          alt="User Avatar"
          class="h-8 w-8 rounded-full"
          data-testid="user-avatar-image"
        />
        <div
          v-else
          class="bg-primary flex h-8 w-8 items-center justify-center rounded-full"
          data-testid="user-avatar-default-icon-container"
        >
          <AppIcon name="uil:user" size="md" color="white" />
        </div>
        <div v-if="!isCollapsed" class="ml-2 text-left" data-testid="user-info">
          <p
            class="text-left text-sm font-semibold text-black"
            data-testid="user-name"
          >
            {{ userName }}
          </p>
          <p
            class="font-base text-left text-xs text-black"
            data-testid="user-mail"
          >
            {{ userMail }}
          </p>
        </div>
      </div>
      <AppIcon
        v-if="!isCollapsed"
        :name="
          isMobile
            ? isMenuOpen
              ? 'fa6-solid:chevron-down'
              : 'fa6-solid:chevron-up'
            : isMenuOpen
              ? 'fa6-solid:chevron-left'
              : 'fa6-solid:chevron-right'
        "
        size="sm"
        color="basics-icon"
        data-testid="user-menu-chevron"
      />
    </button>

    <div
      v-if="isMenuOpen"
      ref="flyoutMenuRef"
      :class="flyoutMenuClasses"
      role="menu"
      tabindex="-1"
      :aria-label="flyoutMenuLabel"
      data-testid="flyout-menu"
    >
      <button
        v-for="(item, index) in menuItems"
        :key="index"
        :ref="(el) => setMenuItemRef(el, index)"
        :class="menuItemClasses(item, index)"
        role="menuitem"
        :tabindex="focusedIndex === index ? 0 : -1"
        :data-testid="`user-menu-item-${index}`"
        :data-index="index"
        v-bind="getMenuItemAriaAttributes(item)"
        @click="handleItemClick(item)"
        @mouseenter="focusedIndex = index"
      >
        <div class="flex items-center">
          <AppIcon
            :name="item.icon"
            size="sm"
            color="basics-icon"
            class="mr-2"
          />
          <span>{{ item.label }}</span>
          <AppIcon
            v-if="
              item.to && isExternal(item.to) && !item.to.startsWith('mailto:')
            "
            name="fa6-solid:external-link-alt"
            size="xs"
            color="basics-icon"
            class="ml-auto"
            :aria-hidden="true"
          />
        </div>
      </button>
    </div>
  </div>
</template>
